/**
 * Maison Aria — Style Analysis Page
 * -----------------------------------
 * 1. Reads quiz answers from localStorage
 * 2. Loads products-index.json from theme assets
 * 3. POSTs to Vercel /analysis endpoint (Claude Haiku)
 * 4. Renders archetype, moodboard (if products matched), profile, email capture
 * 5. On email submit: subscribes to Klaviyo with full archetype profile properties
 *    which triggers the automated style report + nurture flow
 *
 * Klaviyo profile properties set:
 *   $email, $first_name (if provided),
 *   style_archetype, style_tagline, style_description,
 *   style_strengths, style_keywords,
 *   recommended_tier, tier_reason,
 *   quiz_completed_at, style_source
 */

document.addEventListener('DOMContentLoaded', async () => {

  /* ----------------------------------------------------------
     Elements
  ---------------------------------------------------------- */
  const loadingEl = document.getElementById('ma-analysis-loading');
  const errorEl   = document.getElementById('ma-analysis-error');
  const resultsEl = document.getElementById('ma-analysis-results');

  function showError() {
    loadingEl.hidden = true;
    loadingEl.classList.add('is__hidden');
    errorEl.hidden = false;
  }

  function showResults() {
    loadingEl.hidden = true;
    loadingEl.classList.add('is__hidden');
    resultsEl.hidden = false;
  }

  /* ----------------------------------------------------------
     1. Read quiz payload from localStorage
  ---------------------------------------------------------- */
  let quizPayload = null;
  try {
    const raw = localStorage.getItem('ma_style_quiz');
    if (raw) quizPayload = JSON.parse(raw);
  } catch {
    localStorage.removeItem('ma_style_quiz');
  }

  if (!quizPayload?.answers?.length) {
    window.location.href = '/pages/style-quiz';
    return;
  }

  /* ----------------------------------------------------------
     2. Check for cached analysis (avoid re-calling on refresh)
  ---------------------------------------------------------- */
  let cachedAnalysis = null;
  try {
    const raw = localStorage.getItem('ma_style_analysis');
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.quiz_version === quizPayload.completed_at) {
        cachedAnalysis = parsed.analysis;
      }
    }
  } catch {
    localStorage.removeItem('ma_style_analysis');
  }

  /* ----------------------------------------------------------
     3. Load products index
  ---------------------------------------------------------- */
  let products = [];
  try {
    const res = await fetch(window.MA_PRODUCTS_INDEX_URL);
    if (res.ok) products = await res.json();
  } catch {
    console.warn('[ma-analysis] Could not load products index');
  }

  /* ----------------------------------------------------------
     4. Call Vercel analysis endpoint (or use cache)
  ---------------------------------------------------------- */
  let analysis = cachedAnalysis;

  if (!analysis) {
    try {
      const res = await fetch(window.MA_ANALYSIS_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers: quizPayload.answers,
          products
        })
      });

      if (!res.ok) throw new Error(`Analysis endpoint error: ${res.status}`);

      const data = await res.json();
      if (!data.success || !data.analysis) throw new Error('Invalid analysis response');

      analysis = data.analysis;

      localStorage.setItem('ma_style_analysis', JSON.stringify({
        quiz_version: quizPayload.completed_at,
        analysis
      }));

    } catch (err) {
      console.error('[ma-analysis] Failed to get analysis:', err);
      showError();
      return;
    }
  }

  /* ----------------------------------------------------------
     5. Render results
  ---------------------------------------------------------- */
  renderAnalysis(analysis, products);
  showResults();

  /* ----------------------------------------------------------
     6. Wire email form with Klaviyo
  ---------------------------------------------------------- */
  wireEmailForm(analysis, quizPayload);

});

/* ============================================================
   RENDER
============================================================ */

function renderAnalysis(analysis, products) {

  // Archetype header
  document.getElementById('ma-archetype-name').textContent    = analysis.archetype_name;
  document.getElementById('ma-archetype-tagline').textContent = analysis.archetype_tagline;

  // Hidden contact form field — full analysis as readable body
  const archetypeInput = document.getElementById('ma-archetype-input');
  if (archetypeInput) {
    archetypeInput.value = [
      `Style Archetype: ${analysis.archetype_name}`,
      `Tagline: ${analysis.archetype_tagline}`,
      ``,
      `Style Personality: ${analysis.description}`,
      ``,
      `Strengths:`,
      ...(analysis.strengths || []).map(s => `- ${s}`),
      ``,
      `Aesthetic Keywords: ${(analysis.aesthetic_keywords || []).join(', ')}`,
      ``,
      `Recommended Tier: ${analysis.recommended_tier}`,
      `Tier Reason: ${analysis.tier_reason}`
    ].join('\n');
  }

  // Persist recommended tier for membership page
  localStorage.setItem('ma_selected_membership', analysis.recommended_tier.toLowerCase());

  // Moodboard — starts hidden, only revealed when real products matched
  const moodboardEl = document.getElementById('ma-moodboard');
  if (moodboardEl && analysis.selected_product_ids?.length && products.length > 0) {
    const selectedProducts = analysis.selected_product_ids
      .map(id => products.find(p => p.id === id))
      .filter(Boolean);

    if (selectedProducts.length > 0) {
      moodboardEl.innerHTML = selectedProducts.map(p => `
        <a href="${p.url}" class="ma-moodboard__item" target="_blank" rel="noopener">
          <img src="${p.image}" alt="${p.title}" loading="lazy">
          <span class="ma-moodboard__title">${p.title}</span>
        </a>
      `).join('');
      moodboardEl.hidden = false;
    }
  }

  // Description
  document.getElementById('ma-description').textContent = analysis.description;

  // Strengths
  const strengthsEl = document.getElementById('ma-strengths');
  strengthsEl.innerHTML = (analysis.strengths || [])
    .map(s => `<li class="ma-analysis__strength-item">${s}</li>`)
    .join('');

  // Keywords
  const keywordsEl = document.getElementById('ma-keywords');
  keywordsEl.innerHTML = (analysis.aesthetic_keywords || [])
    .map(k => `<span class="ma-analysis__keyword">${k}</span>`)
    .join('');

  // Tier recommendation
  const tierEl = document.getElementById('ma-tier');
  tierEl.innerHTML = `
    <p class="ma-analysis__tier-name">${analysis.recommended_tier}</p>
    <p class="ma-analysis__tier-reason">${analysis.tier_reason}</p>
  `;
}

/* ============================================================
   EMAIL FORM — Klaviyo subscribe + Shopify contact fallback
============================================================ */

function wireEmailForm(analysis, quizPayload) {
  const form      = document.getElementById('ma-email-form');
  const successEl = document.getElementById('ma-email-success');

  if (!form) return;

  form.addEventListener('submit', async (e) => {
    e.preventDefault();

    const emailInput = document.getElementById('ma-email-input');
    const email      = emailInput?.value?.trim();
    if (!email) return;

    const submitBtn = form.querySelector('.ma-analysis__email-btn');
    if (submitBtn) {
      submitBtn.disabled    = true;
      submitBtn.textContent = 'Sending...';
    }

    // 1. Subscribe to Klaviyo with full archetype profile
    await subscribeToKlaviyo(email, analysis, quizPayload);

    // 2. Also submit Shopify contact form for your own records
    submitShopifyForm(form, email, analysis);

    // 3. Show success state
    const formEl    = document.querySelector('.ma-analysis__email-form');
    const headingEl = document.querySelector('.ma-analysis__email-heading');
    const subEl     = document.querySelector('.ma-analysis__email-sub');
    if (formEl)    formEl.hidden    = true;
    if (headingEl) headingEl.hidden = true;
    if (subEl)     subEl.hidden     = true;
    if (successEl) successEl.hidden = false;
  });
}

/* ============================================================
   KLAVIYO SUBSCRIBE
   Uses Klaviyo's v3 Subscribe API — public key only, safe in browser.
   Sets full archetype profile as custom properties which are
   available as dynamic variables in Klaviyo email flows.
============================================================ */

async function subscribeToKlaviyo(email, analysis, quizPayload) {
  const apiKey  = window.MA_KLAVIYO_API_KEY;
  const listId  = window.MA_KLAVIYO_LIST_ID;

  if (!apiKey || !listId) {
    console.warn('[ma-klaviyo] Missing API key or List ID');
    return;
  }

  const payload = {
    data: {
      type: 'subscription',
      attributes: {
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: {
                // Core archetype data — use these as dynamic variables
                // in Klaviyo email templates e.g. {{ event.StyleArchetype }}
                StyleArchetype:    analysis.archetype_name,
                StyleTagline:      analysis.archetype_tagline,
                StyleDescription:  analysis.description,
                StyleStrengths:    (analysis.strengths || []).join(' | '),
                StyleKeywords:     (analysis.aesthetic_keywords || []).join(', '),
                RecommendedTier:   analysis.recommended_tier,
                TierReason:        analysis.tier_reason,
                QuizCompletedAt:   quizPayload?.completed_at || new Date().toISOString(),
                StyleSource:       'style-quiz',
                MembershipPageUrl: 'https://www.maisonaria.com/pages/style-membership'
              }
            }
          }
        },
        consented_at: new Date().toISOString()
      },
      relationships: {
        list: {
          data: {
            type: 'list',
            id: listId
          }
        }
      }
    }
  };

  try {
    const res = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + apiKey, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'revision': '2023-12-15'
      },
      body: JSON.stringify(payload)
    });

    if (res.ok || res.status === 202) {
      console.info('[ma-klaviyo] Subscribed:', email);
    } else {
      const err = await res.text();
      console.error('[ma-klaviyo] Subscribe failed:', err);
    }
  } catch (err) {
    console.error('[ma-klaviyo] Network error:', err);
  }
}

/* ============================================================
   SHOPIFY CONTACT FORM FALLBACK
   Submits silently in the background for your Shopify inbox records.
   Non-blocking — UX never depends on this succeeding.
============================================================ */

function submitShopifyForm(form, email, analysis) {
  try {
    const formData = new FormData(form);
    formData.set('contact[email]', email);

    fetch(form.action || '/contact', {
      method: 'POST',
      body: formData,
      headers: { 'Accept': 'application/json' }
    }).catch(() => {
      // Silent — Klaviyo is the primary capture
    });
  } catch {
    // Silent fallback failure is acceptable
  }
}